import { Component } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal';
import phone, { countryPhoneData } from 'phone';
import { Observable, Subscription } from 'rxjs';
import { PHONE_REGEX } from 'src/app/ui/authentication/regex-policy';

import { Dietitian } from '../../../../../../data/dietitian/domain/dietitian';
import { InvitationTemplate } from '../../../../../../data/dietitian/domain/invitation_template/invitation-template';
import {
  AccountStatus,
  FEATURED_COUNTRY_CODE,
  Gender,
} from '../../../../../../data/patient/domain/patient';
import { AccountInformationsViewModel } from '../../../account/routes/account-informations/account-informations.facade';
import { CreatePatientFacade } from './create-patient.facade';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-create-patient-modal',
  templateUrl: './create-patient-modal.component.html',
  styleUrls: ['./create-patient-modal.component.scss'],
  providers: [CreatePatientFacade],
})
export class CreatePatientModalComponent {
  private patientStatusSubscription?: Subscription;
  accountStatus$: Observable<AccountInformationsViewModel>;
  accountStatusSubscription?: Subscription;
  birthday: Date;
  isLoading = false;
  error?: string;
  dietitian: Dietitian;
  form: FormGroup;
  visibleInvitationTemplate: boolean;

  featuredCountries = FEATURED_COUNTRY_CODE.map((code) =>
    countryPhoneData.find((country) => country.country_code === code),
  );
  countries = countryPhoneData;

  invitationTemplate: InvitationTemplate | undefined;
  invalidInvitationTemplate = false;

  phoneValidator: ValidatorFn = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const countryCode = group.get('phoneCountry')?.value ?? '';
    const phoneNumber = group.get('phone')?.value ?? '';
    switch (countryCode) {
      case 'NC': {
        // New Caledonia
        const newCaledoniaRegexp = /^\d{6}$/;
        return newCaledoniaRegexp.test(phoneNumber)
          ? null
          : { invalidPhoneNumber: true };
      }
      default: {
        const country = this.countries.find((c) => c.alpha2 == countryCode);
        const result = phone(phoneNumber, {
          country: country?.alpha3,
        });

        return result.isValid ? null : { invalidPhoneNumber: true };
      }
    }
  };

  constructor(
    private bsModalRef: BsModalRef,
    private facade: CreatePatientFacade,
    private router: Router,
  ) {
    this.accountStatus$ = this.facade.accountStatus$;

    this.form = new FormGroup(
      {
        lastName: new FormControl('', [
          Validators.required,
          Validators.minLength(1),
        ]),
        firstName: new FormControl('', [
          Validators.required,
          Validators.minLength(1),
        ]),
        phoneCountry: new FormControl(this.dietitian?.country ?? '', [
          Validators.required,
        ]),
        phone: new FormControl('', [
          Validators.required,
          Validators.pattern(PHONE_REGEX),
        ]),
        email: new FormControl('', [Validators.email]),
        birthday: new FormControl(''),
        gender: new FormControl(''),
        consultationReason: new FormControl(''),
        sendEmail: new FormControl(false),
        invitation: new FormControl(
          'Bonjour,\n' +
            ' \n' +
            'Je vous propose que nous restions en contact entre les consultations via l’application MonSuiviDiet.\n' +
            ' \n' +
            ' \n' +
            'Quels sont les avantages ?\n' +
            ' \n' +
            '✅ L’application propose un carnet alimentaire digitalisé qui vous permettra de noter vos observations jour après jour, de mieux prendre conscience de votre comportement alimentaire et de repérer plus facilement les situations problématiques. Je pourrai consulter vos repas depuis mon interface ce qui me permettra de vous donner des conseils plus personnalisés en consultation de suivi !\n' +
            ' \n' +
            '✅ Vous pourrez suivre directement sur l’application les objectifs fixés en consultation pour rester motivé·e et mieux évaluer vos difficultés comme vos réussites.\n' +
            ' \n' +
            '✅ Plus besoin d’attendre le prochain rendez-vous, vous pourrez me poser toutes vos questions entre deux consultations.\n' +
            ' \n' +
            'L’utilisation d’un outil digital permet de s’impliquer davantage dans son parcours de soin et d’ancrer les résultats sur le long terme !\n' +
            ' \n' +
            ' \n' +
            'Si vous êtes intéressé·e je vous invite à :',
        ),
        invitationTemplate: new FormControl(0),
        address: new FormControl(''),
        postalCode: new FormControl(''),
        city: new FormControl(''),
        country: new FormControl(''),
      },
      { validators: this.phoneValidator },
    );
    this.form.controls['phone'].valueChanges.subscribe((value) => {
      if (value.indexOf(' ') >= 0) {
        this.form.controls['phone'].setValue(value.replaceAll(' ', ''));
      }
    });

    this.accountStatusSubscription = this.accountStatus$.subscribe((state) => {
      this.dietitian = state.dietitian;

      if (!this.form?.get('phoneCountry')?.value) {
        this.form?.patchValue({
          phoneCountry: this.dietitian?.country?.toUpperCase(),
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.patientStatusSubscription?.unsubscribe();
  }

  onBirthdayChange(target: any): void {
    this.birthday = new Date(target.value);
  }

  hideModal() {
    this.bsModalRef.hide();
  }

  async createPatient() {
    if (!this.isLoading) {
      this.isLoading = true;
      if (!this.dietitian || !this.form.valid) {
        this.isLoading = false;
        return;
      }
      this.error = undefined;
      const gender: Gender | undefined =
        this.form.get('gender')?.value === ''
          ? undefined
          : <Gender>this.form.get('gender')?.value;

      const countryCode = this.form.get('phoneCountry')?.value ?? '';

      let phoneNumber: string | undefined;
      if (countryCode === 'NC') {
        phoneNumber = `+687${this.form.get('phone')?.value ?? ''}`;
      } else {
        const country = this.countries.find((c) => c.alpha2 == countryCode);
        phoneNumber =
          phone(this.form.get('phone')?.value ?? '', {
            country: country?.alpha2,
          }).phoneNumber ?? undefined;
      }
      try {
        const patient = await this.facade.createPatient(
          {
            dietId: this.dietitian.id.toString(),
            firstName: this.form.get('firstName')?.value,
            lastName: this.form.get('lastName')?.value,
            gender: gender,
            birthday: this.birthday ?? undefined,
            email: this.form.get('email')?.value,
            phoneNumber,
            consultationReason:
              this.form.get('consultationReason')?.value === ''
                ? undefined
                : this.form.get('consultationReason')?.value,
            status: this.form.get('sendEmail')?.value
              ? AccountStatus.INVITATION_SENT
              : AccountStatus.DRAFT,
            unreadDiariesCount: 0,
            lastUnreadDiaryCreatedAt: undefined,
            isActive: false,
            address: this.form.get('address')?.value,
            postalCode: this.form.get('postalCode')?.value,
            city: this.form.get('city')?.value,
            country: this.form.get('country')?.value,
          },
          this.dietitian.dietitianId.id.toString(),
          this.form.get('sendEmail')?.value,
          this.invitationTemplate,
        );

        this.router
          .navigate(['patient', patient.id.toString(), 'dashboard'])
          .then(() => this.hideModal());
      } catch (error) {
        if (error instanceof Error) {
          this.error = error.message;
        }
      }
      this.isLoading = false;
    }
  }

  get lastNameInvalid(): boolean | undefined {
    return (
      !this.form.get('lastName')?.valid &&
      (this.form.get('lastName')?.touched || this.form.get('lastName')?.dirty)
    );
  }

  get sendEmailInvalid(): boolean | undefined {
    return (
      !this.form.get('sendEmail')?.valid &&
      (this.form.get('sendEmail')?.touched || this.form.get('sendEmail')?.dirty)
    );
  }

  get firstNameInvalid(): boolean | undefined {
    return (
      !this.form.get('firstName')?.valid &&
      (this.form.get('firstName')?.touched || this.form.get('firstName')?.dirty)
    );
  }

  get phoneInvalid(): boolean | undefined {
    return (
      (!this.form.get('phone')?.valid ||
        this.form.hasError('invalidPhoneNumber')) &&
      (this.form.get('phone')?.touched || this.form.get('phone')?.dirty)
    );
  }

  get emailInvalid(): boolean | undefined {
    return (
      !this.form.get('email')?.valid &&
      (this.form.get('email')?.touched || this.form.get('email')?.dirty)
    );
  }

  get birthdayInvalid(): boolean | undefined {
    return (
      !this.form.get('birthday')?.valid &&
      (this.form.get('birthday')?.touched || this.form.get('birthday')?.dirty)
    );
  }

  get genderInvalid(): boolean | undefined {
    return (
      !this.form.get('gender')?.valid &&
      (this.form.get('gender')?.touched || this.form.get('gender')?.dirty)
    );
  }

  get consultationReasonInvalid(): boolean | undefined {
    return (
      !this.form.get('consultationReason')?.valid &&
      (this.form.get('consultationReason')?.touched ||
        this.form.get('consultationReason')?.dirty)
    );
  }

  onSendEmailChange(event: any) {
    this.visibleInvitationTemplate = event.target.checked as boolean;
    if (!this.visibleInvitationTemplate) {
      this.invitationTemplate = undefined;
      this.invalidInvitationTemplate = false;
    }
  }

  onChangeInvitationTemplate(template: InvitationTemplate) {
    this.invitationTemplate = template;
  }

  enabledSendEmail() {
    return (
      (!!this.form.get('email')?.value && this.form.get('email')?.valid) ??
      false
    );
  }

  get patientPhoneNumber(): string | undefined {
    const countryCode = this.form.get('phoneCountry')?.value ?? '';
    let phoneNumber: string | undefined;
    if (countryCode === 'NC') {
      phoneNumber = `+687${this.form.get('phone')?.value ?? ''}`;
    } else {
      const country = this.countries.find((c) => c.alpha2 == countryCode);
      phoneNumber =
        phone(this.form.get('phone')?.value ?? '', {
          country: country?.alpha2,
        }).phoneNumber ?? undefined;
    }
    return phoneNumber;
  }

  get addressInvalid(): boolean | undefined {
    return (
      !this.form.get('address')?.valid &&
      (this.form.get('address')?.touched || this.form.get('address')?.dirty)
    );
  }

  get postalCodeInvalid(): boolean | undefined {
    return (
      !this.form.get('postalCode')?.valid &&
      (this.form.get('postalCode')?.touched ||
        this.form.get('postalCode')?.dirty)
    );
  }

  get cityInvalid(): boolean | undefined {
    return (
      !this.form.get('city')?.valid &&
      (this.form.get('city')?.touched || this.form.get('city')?.dirty)
    );
  }

  get countryInvalid(): boolean | undefined {
    return (
      !this.form.get('country')?.valid &&
      (this.form.get('country')?.touched || this.form.get('country')?.dirty)
    );
  }
}
